import type { Middleware } from '@reduxjs/toolkit';

/**
 * Action and timing logger.
 *
 * Two jobs. In development it prints a grouped, timed trace of every action.
 * Everywhere it maintains a small ring buffer of recent actions that can be
 * dumped when something goes wrong, which is far more useful than a stack trace
 * for a bug that only reproduces after a particular sequence of map
 * interactions.
 *
 * Timing is recorded per action because reducer cost is one of the few things
 * on the critical path we fully control - if a selector or reducer starts
 * showing up here at multiple milliseconds, that is a real regression.
 */
export interface LoggedAction {
  readonly type: string;
  readonly at: number;
  readonly durationMs: number;
}

const RING_SIZE = 100;
const ring: LoggedAction[] = [];

/** Recent actions, oldest first. Exposed for diagnostics and error reports. */
export function recentActions(): readonly LoggedAction[] {
  return ring.slice();
}

/** Actions logged at high frequency; traced but never printed individually. */
const NOISY = new Set(['map/viewMoved', 'measure/previewUpdated']);

export const loggerMiddleware: Middleware = () => (next) => (action) => {
  const type =
    typeof action === 'object' && action !== null && 'type' in action
      ? String((action as { type: unknown }).type)
      : 'unknown';

  const started = performance.now();
  const result = next(action);
  const durationMs = performance.now() - started;

  ring.push({ type, at: Date.now(), durationMs });
  if (ring.length > RING_SIZE) ring.shift();

  if (import.meta.env.DEV && !NOISY.has(type)) {
    const cost = durationMs.toFixed(2);
    // eslint-disable-next-line no-console
    console.debug(`%c${type}%c ${cost}ms`, 'color:#004e9a;font-weight:600', 'color:#888');
  }

  return result;
};
